import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AdminCCLChanges() {
  const [meta, setMeta] = useState(null);
  const [changes, setChanges] = useState([]);
  const [totalChanges, setTotalChanges] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);

  const load = async () => {
    try {
      const [m, c] = await Promise.all([
        api.get("/ccl/meta"),
        api.get("/ccl/changes", { params: { limit: 200 } }),
      ]);
      setMeta(m.data);
      setChanges(c.data.changes);
      setTotalChanges(c.data.total ?? c.data.count ?? 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/ccl/sync");
      toast.success(`Sync complete — ${r.data?.meta?.entry_count || "?"} entries`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const sendTestAlert = async () => {
    setTestingAlert(true);
    try {
      const r = await api.post("/admin/ccl/test-alert");
      if (r.data?.sent) {
        toast.success(`Test alert sent (email id ${r.data?.email_id?.slice(0, 8)}…)`);
      } else {
        toast.error(`Alert not delivered: ${r.data?.reason || "unknown reason"}`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Test alert failed");
    } finally {
      setTestingAlert(false);
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div className="min-h-screen bg-ivory">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <Link to="/admin" className="text-[11px] uppercase tracking-[0.22em] text-ink-soft hover:text-brass transition-colors">← Admin</Link>
            <h1 className="font-serif text-3xl text-ink mt-2">CCL Sync &amp; Changes</h1>
          </div>
          <div className="flex gap-3">
          <Button onClick={runSync} disabled={syncing} data-testid="admin-ccl-sync-btn">
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
          <Button variant="outline" onClick={sendTestAlert} disabled={testingAlert} data-testid="admin-ccl-test-alert-btn">
            {testingAlert ? "Sending…" : "Send test alert"}
          </Button>
          </div>
        </div>

        {/* Meta */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 border-y border-hairline py-6 mb-4" data-testid="admin-ccl-meta">
          <Stat label="Entries" value={meta?.entry_count ?? "—"} />
          <Stat label="Last synced" value={fmt(meta?.last_synced)} />
          <Stat label="eCFR as-of" value={meta?.source_as_of || "—"} />
          <Stat label="Last checked" value={fmt(meta?.last_checked)} />
        </div>
        <p className="text-[11px] text-ink-soft mb-10 max-w-3xl">
          <span className="font-semibold uppercase tracking-wider text-[10px]">Alerts:</span>{" "}
          Change-detection emails go to <code className="font-mono">{"CCL_ALERT_RECIPIENT"}</code> configured in the backend env. Delivery requires the recipient domain to be verified in the Resend account tied to the Emergent email key. If the &ldquo;Send test alert&rdquo; button fails with a delivery reason, verify the domain (or route dev alerts to <code className="font-mono">delivered@resend.dev</code>).
        </p>

        {/* Changes table */}
        <h2 className="font-serif text-xl text-ink mb-4">
          Recent changes {totalChanges > 0 && <span className="text-ink-soft font-sans text-[13px] font-normal">({totalChanges.toLocaleString()} total · showing {changes.length})</span>}
        </h2>
        {loading ? (
          <p className="text-ink-soft">Loading…</p>
        ) : changes.length === 0 ? (
          <p className="text-ink-soft">No changes recorded yet.</p>
        ) : (
          <table className="w-full text-[13px]" data-testid="admin-ccl-changes-table">
            <thead>
              <tr className="text-left border-b border-hairline">
                <th className="py-2 pr-4 text-[10px] uppercase tracking-[0.22em] text-ink-soft font-semibold">When</th>
                <th className="py-2 pr-4 text-[10px] uppercase tracking-[0.22em] text-ink-soft font-semibold">ECCN</th>
                <th className="py-2 pr-4 text-[10px] uppercase tracking-[0.22em] text-ink-soft font-semibold">Kind</th>
                <th className="py-2 pr-4 text-[10px] uppercase tracking-[0.22em] text-ink-soft font-semibold">eCFR as-of</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c, i) => (
                <tr key={i} className="border-b border-hairline-soft" data-testid={`admin-ccl-change-${c.eccn}-${i}`}>
                  <td className="py-2 pr-4 text-ink-soft">{fmt(c.at)}</td>
                  <td className="py-2 pr-4">
                    <Link to={`/ccl/e/${c.eccn}`} className="font-mono font-semibold text-brass-deep hover:text-brass">{c.eccn}</Link>
                  </td>
                  <td className="py-2 pr-4">
                    <span className={
                      "inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold border " +
                      (c.kind === "added" ? "border-green-700 text-green-800 bg-green-50" :
                       c.kind === "removed" ? "border-red-700 text-red-800 bg-red-50" :
                       "border-hairline text-ink bg-ivory-alt")
                    }>{c.kind}</span>
                  </td>
                  <td className="py-2 pr-4 text-ink-soft">{c.source_as_of || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-ink-soft font-semibold">{label}</p>
      <p className="text-[15px] text-ink mt-1">{value}</p>
    </div>
  );
}
